import { needsProductIdentifiers } from '../../../../../utils/productIdentifiers'
import { senderIdRequired } from '../../../../../utils/senderIdentity'
import { LIMITS, STATE_NAMES } from './constants'
import type {
  Address,
  BookingForm,
  Customer,
  Errors,
  Item,
  Kind,
  Parcel,
  Party,
  Step,
  Who,
} from './types'

/* ---------- Blank state ---------- */

export const blankParcel = (): Parcel => ({ w: '', l: '', wd: '', h: '', preset: '' })
export const blankAddress = (): Address => ({ l1: '', l2: '', city: '', state: '', pc: '' })
export const blankItem = (): Item => ({
  desc: '',
  qty: '1',
  price: '',
  weight: '',
  code: null,
  opts: [],
  hs: 'idle',
  material: '',
  usage: '',
  pid: { merchant: '', manufacturer: '', standardisedManufacturer: '' },
})
export const blankParty = (): Party => ({
  mode: '',
  first: '',
  last: '',
  company: '',
  phone: '',
  email: '',
  addr: blankAddress(),
  addrMode: 'search',
  idImage: '',
  idPreview: null,
})
export const blankForm = (kind?: Kind): BookingForm => ({
  from: kind === 'import' ? '' : 'NG',
  to: kind === 'domestic' || kind === 'import' ? 'NG' : '',
  fromState: '',
  toState: '',
  type: 'package',
  parcels: [blankParcel()],
  contents: '',
  value: '',
  purpose: '',
  currency: 'NGN',
  photo: '',
  sender: blankParty(),
  receiver: blankParty(),
  handover: { mode: '', date: '' },
  items: [blankItem()],
  extras: { bubble: 0, vacuum: 0, phyto: false, protect: false },
  pay: '',
  declare: false,
})

/* ---------- Numbers & dates ---------- */

export const num = (v: unknown) => {
  const n = parseFloat(String(v ?? ''))
  return isNaN(n) ? 0 : n
}
export const naira = (n: number) =>
  `₦${Number(n || 0).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const pad = (n: number) => String(n).padStart(2, '0')
export const toISO = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const fromISO = (s: string) => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}
export const fmtDay = (d: Date) =>
  d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })

/** Same rule as the old forms: no Sundays, and after 12 noon the earliest is tomorrow. */
export const minPickup = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  if (new Date().getHours() >= 12) d.setDate(d.getDate() + 1)
  while (d.getDay() === 0) d.setDate(d.getDate() + 1)
  return d
}
export const nextPickupDays = (n: number) => {
  const out: Date[] = []
  const d = minPickup()
  while (out.length < n) {
    if (d.getDay() !== 0) out.push(new Date(d))
    d.setDate(d.getDate() + 1)
  }
  return out
}
export const dayLabel = (d: Date) => {
  const today = new Date()
  const tomorrow = new Date()
  tomorrow.setDate(today.getDate() + 1)
  const rel =
    toISO(d) === toISO(today) ? 'Today' : toISO(d) === toISO(tomorrow) ? 'Tomorrow' : ''
  return rel
    ? `${rel}, ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : fmtDay(d)
}

/* ---------- Derived ---------- */

export const deliveryKind = (from: string, to: string): Kind | 'invalid' | null => {
  if (!from || !to) return null
  if (from === 'NG' && to === 'NG') return 'domestic'
  if (from === 'NG') return 'export'
  if (to === 'NG') return 'import'
  return 'invalid'
}
export const formKind = (f: BookingForm) => deliveryKind(f.from, f.to)
export const isIntl = (f: BookingForm) => ['export', 'import'].includes(formKind(f) || '')
export const needsItems = (f: BookingForm) => isIntl(f) && f.type === 'package'
export const stepsFor = (f: BookingForm): Step[] => [
  'route',
  'sender',
  'receiver',
  ...(needsItems(f) ? (['items'] as Step[]) : []),
  'review',
]
export const needsPids = (f: BookingForm) =>
  formKind(f) === 'export' && needsProductIdentifiers(f.to, f.type)

/** On an import the account holder is the receiver; otherwise the sender. */
export const ownerSide = (f: BookingForm): Who =>
  formKind(f) === 'import' ? 'receiver' : 'sender'
export const partyCountry = (f: BookingForm, who: Who) =>
  who === 'sender' ? f.from : f.to

// Address line 2 only reaches the API for the export receiver and both import parties
export const hasLine2 = (f: BookingForm, who: Who) =>
  (formKind(f) === 'export' && who === 'receiver') || formKind(f) === 'import'
export const line1Max = (f: BookingForm, who: Who) =>
  formKind(f) === 'export' && who === 'receiver' ? LIMITS.line1ExportReceiver : LIMITS.line1

export const actualKg = (f: BookingForm) => f.parcels.reduce((t, p) => t + num(p.w), 0)
export const volKg = (p: Parcel) => (num(p.l) * num(p.wd) * num(p.h)) / 5000
export const itemsKg = (f: BookingForm) => f.items.reduce((t, i) => t + num(i.weight), 0)
export const itemsValue = (f: BookingForm) =>
  f.items.reduce((t, i) => t + num(i.qty) * num(i.price), 0)
export const addrLine = (a: Address) =>
  [a.l1, a.l2, a.city, a.state, a.pc].filter(Boolean).join(', ')

/** Digits only; a Nigerian number loses its 0 or 234 prefix so it isn't sent as +2340… */
export const cleanPhone = (phone: string, country: string) => {
  const digits = String(phone || '').replace(/\D/g, '')
  if (country !== 'NG') return digits
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1)
  if (digits.length === 13 && digits.startsWith('234')) return digits.slice(3)
  return digits
}

/**
 * The party as it will be submitted. Picking the account holder copies the
 * customer's contact into the party; anything still blank (e.g. a draft that
 * never had it) falls back to the customer.
 */
export const resolveParty = (f: BookingForm, who: Who, customer: Customer | null): Party => {
  const p = f[who]
  if (ownerSide(f) === who && p.mode === 'owner' && customer) {
    return {
      ...p,
      first: p.first || customer.firstname || '',
      last: p.last || customer.lastname || '',
      phone: p.phone || cleanPhone(customer.phonenumber || '', 'NG'),
      email: p.email || customer.email || '',
    }
  }
  return p
}
/** Whether a party's address is complete enough to price against. */
export const addressReady = (f: BookingForm, who: Who, customer: Customer | null) => {
  if (ownerSide(f) === who && !f[who].mode) return false
  const p = resolveParty(f, who, customer)
  const a = p.addr
  return (
    p.addrMode !== 'search' &&
    !!(a.l1.trim() && a.city.trim() && a.pc.trim() && (formKind(f) === 'domestic' || a.state.trim()))
  )
}
export const ownerContact = (customer: Customer) => ({
  first: customer.firstname || '',
  last: customer.lastname || '',
  phone: cleanPhone(customer.phonenumber || '', 'NG'),
  email: customer.email || '',
})

/** The sender in the shape the shared sender-ID helpers expect. */
export const senderForId = (f: BookingForm, customer: Customer | null) => {
  const s = resolveParty(f, 'sender', customer)
  return {
    sender_first_name: s.first,
    sender_last_name: s.last,
    pickup_number: cleanPhone(s.phone, f.from),
    senderIsAccountOwner: !(ownerSide(f) === 'sender' && s.mode === 'other'),
    senderIdImage: s.idImage,
  }
}
export const kindUpper = (f: BookingForm) => String(formKind(f) || '').toUpperCase()

/* ---------- Validation ---------- */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function validate(step: Step, f: BookingForm, customer: Customer | null): Errors {
  const e: Errors = {}
  const k = formKind(f)

  if (step === 'route') {
    if (!customer) e['cust'] = 'Choose the customer this booking is for.'
    if (!f.from) e['route-from'] = 'Choose where it’s coming from.'
    if (!f.to) e['route-to'] = 'Choose where it’s going.'
    else if (k === 'invalid')
      e['route-to'] = 'One end must be Nigeria. We ship into, out of and within Nigeria.'
    if (k === 'domestic') {
      if (!STATE_NAMES.includes(f.fromState)) e['route-fstate'] = 'Choose the state it’s coming from.'
      if (!STATE_NAMES.includes(f.toState)) e['route-tstate'] = 'Choose the state it’s going to.'
    }
    f.parcels.forEach((p, i) => {
      if (!p.w) e[`p${i}-w`] = 'Enter the weight.'
      else if (num(p.w) < 0.5) e[`p${i}-w`] = 'The minimum weight is 0.5 kg.'
      ;(
        [
          ['l', 'length'],
          ['wd', 'width'],
          ['h', 'height'],
        ] as const
      ).forEach(([key, name]) => {
        if (!(num(p[key]) > 0)) e[`p${i}-${key}`] = `Enter the ${name}.`
      })
    })
    if (f.type === 'document' && actualKg(f) > LIMITS.docKg && !e['p0-w'])
      e['p0-w'] = `Documents can weigh up to ${LIMITS.docKg} kg in total. Choose Package if it’s heavier.`
    if (f.contents.trim().length < 3) e['contents'] = 'Describe briefly what’s inside.'
    else if (f.contents.length > LIMITS.contents)
      e['contents'] = `Keep it to ${LIMITS.contents} characters.`
    if (f.type === 'package' && !(num(f.value) > 0))
      e['value'] = 'Enter what the contents are worth, in naira.'
    if (isIntl(f) && !f.purpose) e['purpose'] = 'Choose why it’s being sent.'
  }

  if (step === 'sender' || step === 'receiver') {
    const who = step
    const P = f[who]
    const ownerHere = ownerSide(f) === who
    const cc = partyCountry(f, who)
    if (ownerHere && !P.mode)
      e[`${who}-mode`] = who === 'sender' ? 'Choose who is sending it.' : 'Choose who is receiving it.'
    if (!ownerHere || P.mode === 'other') {
      if (!P.first.trim()) e[`${who}-first`] = 'Enter the first name.'
      if (!P.last.trim()) e[`${who}-last`] = 'Enter the last name.'
      const digits = cleanPhone(P.phone, cc)
      if (cc === 'NG' ? digits.length !== 10 : digits.length < 6 || digits.length > 15)
        e[`${who}-phone`] =
          cc === 'NG'
            ? 'Enter a Nigerian number like 803 123 4567.'
            : 'Enter a phone number of 6 to 15 digits, without the country code.'
      // Receiver email is required; the sender's falls back to the account email
      if (who === 'receiver' && !EMAIL.test(P.email.trim()))
        e[`${who}-email`] = P.email ? 'Enter an email like name@example.com.' : 'Enter the receiver’s email address.'
      if (who === 'sender' && P.email && !EMAIL.test(P.email.trim()))
        e[`${who}-email`] = 'Enter an email like name@example.com, or leave it blank.'
    }
    if (P.mode || !ownerHere) {
      const id = `${who}-addr`
      if (P.addrMode === 'search') e[`${id}-q`] = 'Search for the address and pick it, or enter it manually.'
      else {
        const max = line1Max(f, who)
        if (!P.addr.l1.trim()) e[`${id}-l1`] = 'Enter the street address.'
        else if (P.addr.l1.length > max)
          e[`${id}-l1`] = `Shorten it to ${max} characters${hasLine2(f, who) ? ' and put the rest in line 2' : ''}.`
        if (!P.addr.city.trim()) e[`${id}-city`] = k === 'domestic' ? 'Choose the city or LGA.' : 'Enter the city or town.'
        if (!P.addr.state.trim()) e[`${id}-state`] = cc === 'NG' ? 'Choose the state.' : 'Enter the state or county.'
        if (!P.addr.pc.trim()) e[`${id}-pc`] = 'Enter the postcode.'
        if (P.addrMode === 'confirmed' && Object.keys(e).some((x) => x.startsWith(id)))
          e[`${id}-q`] = 'This address is missing details. Edit it to finish.'
      }
    }
    if (who === 'sender') {
      if (k === 'export' || k === 'domestic') {
        const s = senderForId(f, customer)
        if (customer && senderIdRequired(s, customer as any, kindUpper(f)) && !P.idImage)
          e['sid-in'] = 'Upload a photo of the sender’s ID.'
      }
      if (!f.handover.mode) e['ho-mode'] = 'Choose collection or drop-off.'
      if (f.handover.mode === 'pickup') {
        const d = f.handover.date
        if (!d) e['ho-date'] = 'Choose a collection date.'
        else if (fromISO(d).getDay() === 0)
          e['ho-date'] = `We don’t collect on Sundays. Choose ${fmtDay(nextPickupDays(1)[0])} or later.`
        else if (fromISO(d) < minPickup()) e['ho-date'] = `The earliest collection is ${fmtDay(minPickup())}.`
      }
    }
  }

  if (step === 'items') {
    const pids = needsPids(f)
    f.items.forEach((it, i) => {
      if (it.desc.trim().length < 3) e[`it${i}-desc`] = 'Describe the item.'
      if (!(num(it.qty) >= 1)) e[`it${i}-qty`] = 'Enter at least 1.'
      if (!(num(it.price) > 0)) e[`it${i}-price`] = 'Enter the price of one item.'
      if (!(num(it.weight) > 0)) e[`it${i}-weight`] = 'Enter the weight.'
      if (!it.code && it.desc.trim().length >= 3) e[`it${i}-code`] = 'Choose a customs code.'
      if (pids && (!it.pid.merchant.trim() || !it.pid.manufacturer.trim()))
        e[`it${i}-pid`] = 'Add the seller’s product code and the manufacturer’s part number — EU customs needs both.'
    })
    if (itemsKg(f) > actualKg(f) + 1e-9) e['meter-w'] = 'Item weights add up to more than the parcel.'
    if (f.currency === 'NGN' && itemsValue(f) > num(f.value) + 1e-9)
      e['meter-v'] = 'Item prices add up to more than the declared value.'
  }

  if (step === 'review') {
    if (!f.pay) e['pay'] = 'Choose how the customer is paying.'
    if (!f.declare) e['declare'] = 'Tick the box to confirm the declaration.'
  }
  return e
}
